import React, { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send, CheckCircle } from '@mui/icons-material';
import { requestFormsApi } from '../services/api';
import { RequestFormCreate } from '../types';

const RequestForm: React.FC = () => {
  const [formData, setFormData] = useState<RequestFormCreate>({
    employee_name: '',
    request_text: '',
    reason: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const createMutation = useMutation(
    (data: RequestFormCreate) => requestFormsApi.createRequestForm(data),
    {
      onSuccess: () => {
        setSuccess(true);
        setError('');
        // Reset form
        setFormData({
          employee_name: '',
          request_text: '',
          reason: '',
        });
        // Hide success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      },
      onError: (error: any) => {
        setError(error.response?.data?.detail || 'Failed to submit request form');
        setSuccess(false);
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.employee_name.trim()) {
      setError('Employee name is required');
      return;
    }
    if (!formData.request_text.trim()) {
      setError('Request description is required');
      return;
    }
    if (!formData.reason.trim()) {
      setError('Reason is required');
      return;
    }

    createMutation.mutate(formData);
  };

  const handleChange = (field: keyof RequestFormCreate, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Submit Request Form
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          Fill out the form below to submit a request. An email will be sent to management for review.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            Request submitted successfully! An email has been sent to management.
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Employee Name"
                value={formData.employee_name}
                onChange={(e) => handleChange('employee_name', e.target.value)}
                margin="normal"
                required
                disabled={createMutation.isLoading}
                helperText="Enter the full name of the employee making the request"
              />

              <TextField
                fullWidth
                label="Request"
                value={formData.request_text}
                onChange={(e) => handleChange('request_text', e.target.value)}
                margin="normal"
                required
                multiline
                rows={4}
                disabled={createMutation.isLoading}
                helperText="Describe what you are requesting (e.g., time off, equipment, schedule change)"
              />

              <TextField
                fullWidth
                label="Reason / Justification"
                value={formData.reason}
                onChange={(e) => handleChange('reason', e.target.value)}
                margin="normal"
                required
                multiline
                rows={4}
                disabled={createMutation.isLoading}
                helperText="Explain why this request is needed"
              />

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={createMutation.isLoading ? <CircularProgress size={20} /> : <Send />}
                  disabled={createMutation.isLoading}
                  fullWidth
                >
                  {createMutation.isLoading ? 'Submitting...' : 'Submit Request'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setFormData({
                      employee_name: '',
                      request_text: '',
                      reason: '',
                    });
                    setError('');
                    setSuccess(false);
                  }}
                  disabled={createMutation.isLoading}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> All requests will be sent via email to management for review.
              You will receive a response within 2-3 business days.
            </Typography>
          </Alert>
        </Box>
      </Box>
    </Container>
  );
};

export default RequestForm;
